import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import YearHeatmap from "../components/presence/YearHeatmap";
import JurisdictionTable from "../components/presence/JurisdictionTable";
import CsvImport from "../components/presence/CsvImport";
import OverridesEditor from "../components/presence/OverridesEditor";
import { ChevronLeft, ChevronRight, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  target_met: { label: "TARGET MET", color: "#5E8467" },
  on_track: { label: "ON TRACK", color: "#C15A38" },
  at_risk: { label: "AT RISK", color: "#B24B3E" },
};

function arc(cx, cy, r, f0, f1) {
  const a0 = Math.PI + f0 * Math.PI;
  const a1 = Math.PI + f1 * Math.PI;
  const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
  const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
  return `M ${x0} ${y0} A ${r} ${r} 0 ${f1 - f0 > 0.5 ? 1 : 0} 1 ${x1} ${y1}`;
}

function HalfDonut({ actual, committed, contingent, target }) {
  const denom = Math.max(target, actual + committed + contingent, 1);
  const f = (v) => v / denom;
  let acc = 0;
  const segs = [
    { key: "actual", v: actual, color: "#C15A38" },
    { key: "committed", v: committed, color: "#E0B39C" },
    { key: "contingent", v: contingent, color: "#F0DCCE" },
  ]
    .filter((s) => s.v > 0)
    .map((s) => {
      const p = { ...s, f0: f(acc), f1: f(acc + s.v) };
      acc += s.v;
      return p;
    });
  const tf = f(target);
  const ta = Math.PI + tf * Math.PI;
  return (
    <svg viewBox="0 0 220 118" className="w-full" data-testid="act60-donut">
      <path d={arc(110, 108, 84, 0, 1)} stroke="#E4DDD1" strokeWidth="18" fill="none" />
      {segs.map((s) => (
        <path key={s.key} d={arc(110, 108, 84, s.f0, s.f1)} stroke={s.color} strokeWidth="18" fill="none" data-testid={`donut-${s.key}`} />
      ))}
      {tf < 0.999 && (
        <line
          x1={110 + 70 * Math.cos(ta)} y1={108 + 70 * Math.sin(ta)}
          x2={110 + 98 * Math.cos(ta)} y2={108 + 98 * Math.sin(ta)}
          stroke="#18181B" strokeWidth="2"
        />
      )}
      <text x="110" y="84" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="36" fontWeight="600" fill="#09090B" data-testid="summary-total">
        {actual}
      </text>
      <text x="110" y="104" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="11" fill="#A1A1AA">
        / {target} target
      </text>
    </svg>
  );
}

function ProjectionRows({ act60, projection }) {
  const target = act60.target;
  const rows = [
    { label: "Actual (to date)", value: act60.total, color: "#C15A38", tid: "proj-actual" },
    { label: "Committed (confirmed trips)", value: `+${projection.pr_committed}`, color: "#E0B39C", tid: "proj-committed" },
    { label: "Contingent on planned trips", value: `±${projection.pr_contingent}`, color: "#F0DCCE", tid: "proj-contingent" },
  ];
  const conf = projection.total_confirmed;
  const plan = projection.total_with_planned;
  return (
    <div className="mt-4" data-testid="projection-breakdown">
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 text-[11px] font-mono text-sand-600">
              <span className="w-2.5 h-2.5 inline-block shrink-0" style={{ background: r.color }} /> {r.label}
            </span>
            <span className="font-mono text-xs font-semibold" data-testid={r.tid}>{r.value}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 pt-3 border-t border-sand-100 space-y-1" data-testid="projection-totals">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-mono text-sand-600">Year-end · confirmed only</span>
          <span className={`font-mono text-xs font-semibold ${conf >= target ? "text-success" : "text-error"}`} data-testid="proj-total-confirmed">
            {conf} {conf >= target ? "✓" : `(short ${target - conf})`}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-mono text-sand-600">Year-end · if planned trips happen</span>
          <span className={`font-mono text-xs font-semibold ${plan >= target ? "text-success" : "text-error"}`} data-testid="proj-total-planned">
            {plan} {plan >= target ? "✓" : `(short ${target - plan})`}
          </span>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ act60, unknownDays, projection }) {
  const meta = STATUS_META[act60.status] || { label: "—", color: "#6F655C" };
  const anchored = act60.anchor_days !== null && act60.anchor_days !== undefined;
  return (
    <div className="rounded-card border border-hairline bg-surface h-full flex flex-col" data-testid="act60-summary-card">
      <div className="px-6 py-5 border-b border-sand-200 flex items-start justify-between">
        <p className="label-tag">Act 60 · days in PR</p>
        <span className="font-mono text-[10px] font-semibold tracking-[0.15em] px-2 py-1" style={{ color: meta.color, border: `1px solid ${meta.color}` }} data-testid="summary-status">
          {meta.label}
        </span>
      </div>
      <div className="px-6 py-5 flex-1">
        {projection ? (
          <>
            <HalfDonut
              actual={act60.total}
              committed={projection.pr_committed}
              contingent={projection.pr_contingent}
              target={act60.target}
            />
            <ProjectionRows act60={act60} projection={projection} />
          </>
        ) : (
          <p className="font-mono text-6xl font-semibold text-sand-950 leading-none" data-testid="summary-total">
            {act60.total}
            <span className="text-xl text-sand-400"> / {act60.target}</span>
          </p>
        )}
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 mt-5">
          <div><p className="label-tag text-[9px] mb-1">Needed</p><p className="font-mono text-sm">{act60.needed}</p></div>
          <div><p className="label-tag text-[9px] mb-1">Days left in year</p><p className="font-mono text-sm">{act60.days_left}</p></div>
          <div><p className="label-tag text-[9px] mb-1">Nights in PR</p><p className="font-mono text-sm">{act60.nights_pr}</p></div>
          <div><p className="label-tag text-[9px] mb-1">Unknown days</p><p className="font-mono text-sm">{unknownDays}</p></div>
        </div>
      </div>
      <div className="border-t border-sand-200 px-6 py-4">
        {anchored ? (
          <p className="text-xs text-sand-600 leading-relaxed" data-testid="anchor-info">
            <span className="font-mono font-semibold text-sand-900">{act60.anchor_days} days</span> anchored as of{" "}
            <span className="font-mono">{act60.anchor_as_of}</span> +{" "}
            <span className="font-mono font-semibold text-sand-900">{act60.engine_days_after_anchor}</span> engine-counted since.
            Clear the anchor in Profile once full history is uploaded.
          </p>
        ) : (
          <p className="text-xs text-sand-600 leading-relaxed" data-testid="anchor-info">
            Fully engine-computed from flight history. Set a YTD anchor in Profile if history is incomplete.
          </p>
        )}
        <Link to="/you/profile" className="font-mono text-[10px] font-semibold uppercase tracking-wider text-ember hover:underline inline-flex items-center gap-1.5 mt-2" data-testid="edit-anchor-link">
          <SlidersHorizontal size={11} /> Edit anchor in Profile
        </Link>
      </div>
    </div>
  );
}

export default function Act60() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/presence/summary?year=${year}`);
      setSummary(data);
    } catch (e) {
      toast.error("Could not load presence data");
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <main className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-8 md:py-12" data-testid="act60-page">
        <div className="mb-8 md:mb-10 flex items-end justify-between gap-4">
          <div>
            <p className="label-tag mb-3">Physical presence</p>
            <h1 className="heading-h1 text-4xl sm:text-5xl lg:text-6xl">Act 60 Tracker.</h1>
            <div className="divider-strong mt-4" />
          </div>
          <div className="flex items-center gap-1 pb-2" data-testid="year-switcher">
            <button onClick={() => setYear((y) => y - 1)} className="btn-swiss btn-ghost p-2" data-testid="year-prev">
              <ChevronLeft size={16} />
            </button>
            <span className="font-mono text-lg font-semibold px-2" data-testid="year-label">{year}</span>
            <button
              onClick={() => setYear((y) => Math.min(currentYear, y + 1))}
              disabled={year >= currentYear}
              className="btn-swiss btn-ghost p-2 disabled:opacity-30"
              data-testid="year-next"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {summary === null ? (
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 lg:col-span-4 rounded-card border border-hairline bg-surface h-80 animate-pulse" />
            <div className="col-span-12 lg:col-span-8 rounded-card border border-hairline bg-surface h-80 animate-pulse" />
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 lg:col-span-4 fade-up fade-up-1">
              <SummaryCard act60={summary.act60} unknownDays={summary.unknown_days} projection={summary.projection} />
            </div>
            <div className="col-span-12 lg:col-span-8 fade-up fade-up-2">
              <YearHeatmap timeline={summary.timeline} projection={summary.projection} year={summary.year} />
            </div>
            <div className="col-span-12 lg:col-span-5 fade-up fade-up-3">
              <JurisdictionTable jurisdictions={summary.jurisdictions} transitNights={summary.transit_nights} />
            </div>
            <div className="col-span-12 lg:col-span-7 fade-up fade-up-4">
              <CsvImport onImported={load} />
            </div>
            <div className="col-span-12 fade-up fade-up-5">
              <OverridesEditor onChanged={load} />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
